import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useCloseTicket } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

type CloseTicketPopupT = {
  isCloseTicketPopupOpened: boolean;
  handleClosePopup: NoopT;
  isSelectedTicketCountEmpty: boolean;
  isClosingTicket: boolean;
  handleCloseTicket: (ticketIds: string[], requestRO: boolean) => NoopT;
};

const useCloseTicketPopup = (): CloseTicketPopupT => {
  const {
    selectedTicketsIds,
    setCloseTicketPopupVisibility,
    isCloseTicketPopupOpened,
    setSelectedTicketsIds,
  } = useTickets();

  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  /**
   * Api hooks
   */
  const closeTicketMutation = useCloseTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
    },
  });

  /**
   * Handlers
   */
  const handleClosePopup = useCallback(() => {
    setCloseTicketPopupVisibility(false);
    setSelectedTicketsIds([]);
  }, [setCloseTicketPopupVisibility, setSelectedTicketsIds]);

  const handleCloseTicket = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_ticketIds: string[], _requestRo: boolean): (() => void) =>
      (): void => {
        closeTicketMutation.mutate(
          { ticketIds: selectedTicketsIds, requestRo: false },
          {
            onSuccess: () => {
              dispatch(
                updateSnackbarNotification({
                  snackbarMessage: 'Ticket successfully closed',
                  snackBarSeverity: 'success',
                })
              );
              handleClosePopup();
            },
            onError: error => {
              const errorMsg =
                getErrorMessage(error as ApiErrorI) ||
                getValidationErrorMessage(error as ApiErrorI);
              dispatch(
                updateSnackbarNotification({
                  snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
                  snackBarSeverity: 'error',
                })
              );
              handleClosePopup();
            },
          }
        );
      },
    [closeTicketMutation, dispatch, handleClosePopup, selectedTicketsIds]
  );

  return {
    handleClosePopup,
    handleCloseTicket,
    isCloseTicketPopupOpened,
    isSelectedTicketCountEmpty: !selectedTicketsIds.length,
    isClosingTicket: closeTicketMutation.isLoading,
  };
};

export default useCloseTicketPopup;
